import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, LayersControl, ScaleControl, ZoomControl } from 'react-leaflet';
import LayerRenderer from './LayerRenderer';
import 'leaflet/dist/leaflet.css';
import './MapEngine.css';

// ── Default category config for CIP harvest records ───────────────────────────
const HARVEST_DATA_TYPES = {
  plants_medicine: { name: 'Plants & Medicine', icon: '🌿', color: '#22c55e' },
  hunting:         { name: 'Hunting',            icon: '🦌', color: '#92400e' },
  birds:           { name: 'Birds',              icon: '🦅', color: '#0ea5e9' },
  fishing:         { name: 'Fishing',            icon: '🐟', color: '#2563eb' },
  environmental:   { name: 'Environmental',      icon: '🌍', color: '#16a34a' },
  other:           { name: 'Other',              icon: '📍', color: '#6b7280' },
};

/**
 * MapEngine — unified map for CIP data layers.
 *
 * Props:
 *   dataLayers      — [{ dataTypeId: string, data: [], config?: {} }]
 *                     If config is omitted, HARVEST_DATA_TYPES[dataTypeId] is used.
 *   center          — [lat, lng]  default: [49.895, -97.138] (Winnipeg, MB)
 *   zoom            — initial zoom, default 8
 *   height          — CSS string, default '520px'
 *   enabledLayers   — array of initially-enabled dataTypeIds (default: all)
 *   onFeatureClick  — callback(record)
 */
const MapEngine = ({
  dataLayers = [],
  center = [49.895, -97.138],
  zoom = 8,
  height = '520px',
  enabledLayers = null,
  onFeatureClick = null,
}) => {
  const [activeLayers, setActiveLayers] = useState(() => {
    if (enabledLayers) return new Set(enabledLayers);
    return new Set(dataLayers.map(l => l.dataTypeId));
  });

  // Update activeLayers when dataLayers changes (new layer added)
  useEffect(() => {
    if (enabledLayers !== null) return;
    setActiveLayers(prev => {
      const next = new Set(prev);
      dataLayers.forEach(l => { if (!next.has(l.dataTypeId)) next.add(l.dataTypeId); });
      return next;
    });
  }, [dataLayers, enabledLayers]);

  const toggle = (id) =>
    setActiveLayers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const getConfig = (layer) =>
    layer.config || HARVEST_DATA_TYPES[layer.dataTypeId] || { name: layer.dataTypeId, icon: '📍', color: '#94a3b8' };

  const totalFeatures = dataLayers.reduce((s, l) => s + (l.data?.length || 0), 0);

  return (
    <div className="map-engine" style={{ height, position: 'relative', display: 'flex' }}>
      {/* Layer control sidebar */}
      <div className="map-engine__sidebar">
        <p className="map-engine__sidebar-title">Map Layers</p>
        {dataLayers.map(layer => {
          const cfg   = getConfig(layer);
          const on    = activeLayers.has(layer.dataTypeId);
          const count = layer.data?.length ?? 0;
          return (
            <div
              key={layer.dataTypeId}
              className={`map-engine__layer-toggle${on ? ' active' : ''}`}
              onClick={() => toggle(layer.dataTypeId)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && toggle(layer.dataTypeId)}
            >
              <span style={{ fontSize: 18 }}>{cfg.icon}</span>
              <span className="map-engine__layer-name">{cfg.name}</span>
              <span className="map-engine__layer-count">{count}</span>
              <span className="map-engine__layer-dot" style={{ background: on ? cfg.color : '#cbd5e1' }} />
            </div>
          );
        })}
        <div className="map-engine__stats">
          <span>Total: {totalFeatures}</span>
          <span>Active: {activeLayers.size}</span>
        </div>
      </div>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={center}
          zoom={zoom}
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topographic">
              <TileLayer
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {dataLayers.map(layer => {
            if (!activeLayers.has(layer.dataTypeId)) return null;
            return (
              <LayerRenderer
                key={layer.dataTypeId}
                dataTypeConfig={getConfig(layer)}
                data={layer.data || []}
                onFeatureClick={onFeatureClick}
              />
            );
          })}

          <ZoomControl position="bottomright" />
          <ScaleControl position="bottomleft" imperial={false} />
        </MapContainer>
      </div>
    </div>
  );
};

export default MapEngine;
